import { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import queryString from 'query-string'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { setActiveZone } from '../../features/Zone/zoneThunks'
import { loadMockUserCoords } from '../../shared/utils/loadMockUserCoords.utils'
import { CaseZone, Zone, ZoneTypeEnum } from '../../shared/types'
import { getFilePath } from '../../shared/utils'
import { clearZoneError } from '../../features/Zone/zoneSlice'

const useIntro = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { zoneId } = useParams<{ zoneId: string }>()

  // get query params
  const location = useLocation()
  const queryParams = queryString.parse(location.search)

  // =======================
  // Selectors
  // =======================
  const {
    data: detectedZonesData,
    loading: detectedZonesLoading,
    error: detectedZonesError,
    userCoords,
  } = useAppSelector((state) => state.detectedZones)
  const {
    data: activeZoneData,
    loading: activeZoneLoading,
    error: activeZoneError,
  } = useAppSelector((state) => state.zone)
  const activeZoneId = activeZoneData?.id

  const introZoneData =
    detectedZonesData &&
    (detectedZonesData.find((zone) => zoneId === zone.id) as Zone)

  // =======================
  // State
  // =======================
  const [mockUserCoords, setMockUserCoords] = useState<any>()

  // =======================
  // Effects
  // =======================
  useEffect(() => {
    const fetchCoords = async () => {
      if (queryParams?.lat && queryParams?.lng) {
        console.log('Does this get used?')
      } else if (process.env.REACT_APP_USE_MOCK_COORDS === 'true') {
        const data = await loadMockUserCoords()
        setMockUserCoords(data)
      }
    }
    fetchCoords()
  }, [])

  useEffect(() => {
    dispatch(clearZoneError())
  }, [dispatch])

  // =======================
  // Props
  // =======================
  const filePath = getFilePath({ zoneId, folderId: 'intro' })

  const caseData = useMemo<CaseZone | null>(() => {
    if (detectedZonesLoading || activeZoneLoading) return null
    if (!detectedZonesData) return null
    const zone = detectedZonesData.find((zone) => zone.id === zoneId)
    return zone?.type === ZoneTypeEnum.Case ? (zone as CaseZone) : null
  }, [detectedZonesData, detectedZonesLoading, activeZoneLoading, zoneId])

  const error = useMemo(() => {
    if (activeZoneError) return activeZoneError
    if (detectedZonesError) return detectedZonesError
    return undefined
  }, [activeZoneError, detectedZonesError])

  const redirectToDashboard = () => {
    history.push({
      pathname: `/`,
      search: location.search, // preserves ?lat=...&lng=...
    })
  }

  // =======================
  // Handlers
  // =======================
  const handleStartZone = async (zoneId: string) => {
    if (activeZoneId === zoneId) {
      // If this is the user active zone/case, then send to dashboard/map
      redirectToDashboard()
      return
    } else if (activeZoneId) {
      // If this is NOT the user active zone/case, then ask if they want to change cases
      const updateUserZone = window.confirm(
        `You are already working the ${activeZoneData?.name} case. Are you sure you want to start ${caseData?.name}?`
      )
      if (!updateUserZone) return
    }
    // Update the user active zone
    const body = {
      zoneId,
      lat: userCoords?.lat || mockUserCoords?.latitude,
      long: userCoords?.long || mockUserCoords?.longitude,
    }
    const result = await dispatch(setActiveZone(body))
    if (setActiveZone.fulfilled.match(result)) {
      console.log(
        `✅ ${caseData?.name} zone started successfully:`,
        result.payload
      )
      redirectToDashboard()
    } else {
      console.error('❌ Failed to start zone:', result.payload || result.error)
    }
  }

  return {
    zoneId,
    data: caseData,
    loading: detectedZonesLoading || activeZoneLoading,
    error,
    filePath,
    handlers: {
      handleStartZone,
    },
  }
}

export default useIntro
